import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Card, CardMedia, Skeleton, Tooltip } from '@mui/material';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Link, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import type React from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { DragSourceMonitor } from 'react-dnd';
import { useDrag } from 'react-dnd';
import assetsByCollectionIdPlaylist from 'src/api/collection/assetsByCollectionIdPlaylist';
import editCollection from 'src/api/collection/editCollection';
import type { ParsedSuperViewData, SuperViewResponse } from 'src/api/viewModels/data';
import { getSuperViewAugments, parseSuperView } from 'src/api/viewModels/data';
import HighlightedFragment from 'src/components/HighlightedText/HighlightedFragment';
import HighlightedText from 'src/components/HighlightedText/HighlightedText';
import MatchContext from 'src/components/MatchContext/MatchContext';
import SelectCheckbox from 'src/components/SelectCheckBox/SelectCheckBox';
import { TranscriptResults } from 'src/components/transcript/TranscriptResults';
import {
  AUDIO,
  FD_MIMETYPE_SLUG,
  GALLERY_CTYPE,
  HOME_PATH,
  LAYOUT,
  LIBRARY_ASSET_ACTIONS,
  PROXY_FORMAT_SLUG,
  SEARCH_PARAM,
  TWOUP,
  VIDEO,
} from 'src/constants';
import CreatePlayListContext, { type PlaylistAsset } from 'src/context/CreatePlayListContext';
import { buildFormatsQ } from 'src/dynamoQueries/queryHelpers';
import { buildSuperView } from 'src/dynamoQueries/superView';
import { disableContextMenuElem } from 'src/helpers';
import { getAssetDisplayName } from 'src/helpers/assetDisplayName';
import { getMetadataValue } from 'src/helpers/common';
import { DATE_FIELD_TYPES } from 'src/helpers/dateFields';
import { getDensityConfig } from 'src/helpers/densityConfig';
import { useMetaQ } from 'src/hooks/meta';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';
import viewStateVar from 'src/store/viewState';
import type Player from 'video.js/dist/types/player';
import type { AkomiMediaCardProps, GalleryRouteParams, SuperViewField } from '../types';
import ExtendedMetaDataCard from './ExtendedMetadata';
import { AssetActionsPermMenu } from './MediaCardActionsIcon/AssetActionsPermMenu';
import MediaCardInfoIcon from './MediaCardInfoIcon/MediaCardInfoIcon';
import { MediaCardPreviewModal } from './MediaCardPreviewModal';
import MediaCardVideoIcon from './MediaCardVideoIcon/MediaCardVideoIcon';
import MediaRouterComponent from './MediaRouterComponent';
import styles from './Styles';

const idProp = 'akm-media-card';

const AkomiMediaCard: React.FC<AkomiMediaCardProps> = ({ item, ...props }) => {
  const customSearchURL = new URLSearchParams(window.location.search);
  const isOnTwoupView = customSearchURL.get(LAYOUT) === TWOUP;
  const { density } = useReactiveVar(akomiMediaCardGridVar);
  const densityConfig = getDensityConfig(density);
  const classes = styles({ isOnTwoupView, density });
  const [expanded, setExpanded] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const { setCreateAddedAssets } = useContext(CreatePlayListContext);
  const viewState = useReactiveVar(viewStateVar);
  // Use strict: false to handle routes without galleryId param (like /library)
  const params = useParams({ strict: false }) as GalleryRouteParams;
  const openEditCollectionRef = useRef<boolean>(viewState.openEditCollection);
  const selectedCollectionIdRef = useRef<string | null>(viewState.selectedCollectionId);
  const mimetypeRef = useRef<string | undefined>(undefined);
  const superViewNameRef = useRef<string | undefined>(undefined);
  const mediaCardRef = useRef<HTMLDivElement | null>(null);
  const searchTerm = customSearchURL.get(SEARCH_PARAM);
  const hasWords = customSearchURL.getAll('include').filter((a) => !a.includes(' '));
  const hasPhrase = customSearchURL
    .getAll('include')
    .filter((a) => a.includes(' '))
    .toString()
    .replace('+', ' ');

  useEffect(() => {
    setExpanded(viewState.showMetadata);
  }, [viewState.showMetadata]);

  useEffect(() => {
    selectedCollectionIdRef.current = viewState.selectedCollectionId;
  }, [viewState.selectedCollectionId]);

  useEffect(() => {
    openEditCollectionRef.current = viewState.openEditCollection;
  }, [viewState.openEditCollection]);

  const [addItemHandler] = useMutation(editCollection, {
    fetchPolicy: 'network-only',
    refetchQueries: [assetsByCollectionIdPlaylist],
  });

  const addItemCollectionHandler = (selectedAssetForAdding: string): void => {
    addItemHandler({
      variables: {
        collectionId: selectedCollectionIdRef.current,
        assetIds: selectedAssetForAdding,
        collectionType: 'PLAYLIST',
        autoSeq: true,
      },
    });
  };

  const ViewMediaCardMinimalInfo = viewState.viewModels.library.ViewMediaCardMinimalInfo;

  const {
    data: superView,
    loading: superViewLoading,
    refetch: refetchSuperView,
  } = useQuery(buildSuperView(), {
    skip: !item?.id || !ViewMediaCardMinimalInfo?.node?.componentName,
    variables: { globalId: item?.id, viewComponentName: ViewMediaCardMinimalInfo?.node?.componentName },
  });

  useEffect(() => {
    const componentId = `akomi-card-${item?.id}-${Math.random()}`;

    const handleAssetUpdate = (): void => {
      const updateData = localStorage.getItem('assetUpdated');
      if (updateData) {
        try {
          const {
            assetId,
            timestamp,
            processedBy = [],
          } = JSON.parse(updateData) as {
            assetId: string;
            timestamp: number;
            processedBy: string[];
          };

          if (assetId === item?.id && !processedBy.includes(componentId)) {
            refetchSuperView();

            const updatedProcessedBy = [...processedBy, componentId];
            localStorage.setItem(
              'assetUpdated',
              JSON.stringify({
                assetId,
                timestamp,
                processedBy: updatedProcessedBy,
              }),
            );

            setTimeout(() => {
              const currentData = localStorage.getItem('assetUpdated');
              if (currentData) {
                const parsed = JSON.parse(currentData) as { timestamp: number };
                if (parsed.timestamp === timestamp) {
                  localStorage.removeItem('assetUpdated');
                }
              }
            }, 5000);
          }
        } catch {
          const updatedAssetId = updateData;
          if (updatedAssetId === item?.id) {
            refetchSuperView();
            localStorage.removeItem('assetUpdated');
          }
        }
      }
    };

    handleAssetUpdate();

    const interval = setInterval(handleAssetUpdate, 500);

    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === 'assetUpdated') {
        handleAssetUpdate();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [item?.id, refetchSuperView]);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'CARD_AKOMI',
      item: { id: item.id },
      collect: (monitor: DragSourceMonitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      end: (_dragItem: unknown, monitor: DragSourceMonitor) => {
        if (monitor.didDrop() && !openEditCollectionRef.current) {
          const displayName = superViewNameRef.current || item.name || undefined;
          setCreateAddedAssets((prev) => [
            ...prev,
            { id: item.id, name: displayName, mediatype: mimetypeRef.current } as PlaylistAsset,
          ]);
        }
        if (monitor.didDrop() && openEditCollectionRef.current) {
          addItemCollectionHandler(item.id);
        }
      },
    }),
    [item.id, item.name],
  );

  const getMediaPageRoute = useCallback(() => {
    const galleryId = (params as { galleryId?: string })?.galleryId;
    const basePath = `${HOME_PATH}/media-page/${item?.id}`;
    // Use search params instead of state for TanStack Router
    if (galleryId) {
      return `${basePath}?collectionType=${GALLERY_CTYPE}&collectionId=${galleryId}`;
    }
    return basePath;
  }, [item?.id, params]);

  useEffect(() => {
    if (mediaCardRef?.current) {
      disableContextMenuElem(mediaCardRef.current);
    }
  }, []);

  const handleExpandClick = (): void => {
    viewStateVar({
      ...viewState,
      showHorizontal: !viewState.showHorizontal || viewState.horizontalMediaCardIndex !== props.akomiMediaCardIndex,
      horizontalMediaCardDetails: {
        id: item.id,
        name: item.name,
        akomiMediaCardIndex: props.akomiMediaCardIndex,
      } as unknown as typeof viewState.horizontalMediaCardDetails,
      horizontalMediaCardIndex: props.akomiMediaCardIndex,
    });
  };

  const playerRef = useRef<Player | null>(null);
  const setPlayer = (_player: Player): void => {
    playerRef.current = _player;
  };

  const setMediaCardIndex = (): void => {
    viewStateVar({
      ...viewState,
      mediaPageViaPlaylist: false,
      mediaPageViaLibrary: true,
    });
  };

  const { data: proxyFormatData } = useQuery(buildFormatsQ(), {
    skip: !item?.id,
    variables: { asset_Id: item.id, fmtType_Slug: PROXY_FORMAT_SLUG },
  });

  const proxyFormat = proxyFormatData?.formats ? proxyFormatData.formats[0] : {};

  const { data: formatData } = useMetaQ({
    skip: !proxyFormat?.id,
    variables: {
      globalId: proxyFormat?.id,
      definitionSlugIn: [FD_MIMETYPE_SLUG],
    },
  });

  const mimetype = getMetadataValue(formatData as Record<string, unknown> | null | undefined, FD_MIMETYPE_SLUG) as
    | string
    | undefined;
  mimetypeRef.current = mimetype;

  const parsedSuperView = parseSuperView(superView as SuperViewResponse | null | undefined);
  const superViewFields: SuperViewField[] | undefined =
    parsedSuperView.length > 0
      ? parsedSuperView.map((field: ParsedSuperViewData) => ({
          label: field.label ?? '',
          show: field.show ?? '',
          slug: field.slug,
          dataType: field.fieldType,
          fallbackMessage: field.fallbackMessage,
        }))
      : undefined;
  const visibleFields =
    densityConfig.maxVisibleFields != null
      ? superViewFields?.slice(0, densityConfig.maxVisibleFields)
      : superViewFields;
  // Resolved title comes from the backend augments sentinel (deterministic
  // fallback chain over metadata/native columns). `item.name` (passed through
  // from drag data / parent list state) still takes priority over augmentTitle
  // inside getAssetDisplayName; the helper also handles the final
  // "Untitled Asset" → "Untitled" display coercion.
  const augmentTitle = getSuperViewAugments(superView as SuperViewResponse | null | undefined).title;
  superViewNameRef.current = augmentTitle || undefined;
  const cardTitle = getAssetDisplayName({ name: item.name, contentObjectName: augmentTitle }).replace(
    'Untitled Asset',
    'Untitled',
  );

  return (
    <>
      <Card id={idProp} raised={true} className={classes.mainmediacardframe} data-testid="library-card">
        <CardMedia
          ref={(r: HTMLDivElement | null) => {
            drag(r);
            mediaCardRef.current = r;
          }}
          style={{
            opacity: isDragging ? 0.5 : 1,
            cursor: 'move',
          }}
          className={classes.cardMedia}
        >
          <MediaRouterComponent
            itemId={item.id}
            setPlayer={setPlayer}
            onImageClick={() => setShowPreview(true)}
            akomiMediaCardIndex={props.akomiMediaCardIndex}
          />
        </CardMedia>
        <SelectCheckbox itemId={item.id} absolutePosition />
        <AssetActionsPermMenu itemId={item.id} moreActions={!params.galleryId ? LIBRARY_ASSET_ACTIONS : []} />
        <MediaCardInfoIcon itemId={item.id} />
        {(mimetype?.includes(VIDEO) || mimetype?.includes(AUDIO)) && (
          <MediaCardVideoIcon onClickHandler={() => setShowPreview(true)} />
        )}
        <CardContent
          onClick={setMediaCardIndex}
          style={{
            opacity: isDragging ? 0.5 : 1,
            cursor: 'pointer',
          }}
          className={classes.cardContent}
        >
          {superViewLoading ? (
            <>
              <Skeleton
                variant="text"
                animation="wave"
                width="55%"
                sx={{
                  height: 18,
                  bgcolor: 'rgba(255,255,255,0.06)',
                  borderRadius: 0.5,
                  mb: 0.5,
                  '&::after': {
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
                  },
                }}
              />
              {Array.from({ length: densityConfig.skeletonFieldCount }, (_, i) => [25, 35, 20][i % 3]).map((w, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton layout
                <Box key={i} sx={{ display: 'flex', gap: '4px', alignItems: 'center', py: '1px', mb: '2px' }}>
                  <Skeleton
                    variant="text"
                    animation="wave"
                    width={`${w}%`}
                    sx={{
                      height: 14,
                      bgcolor: 'rgba(255,255,255,0.04)',
                      borderRadius: 0.5,
                      '&::after': {
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)',
                      },
                    }}
                  />
                  <Skeleton
                    variant="text"
                    animation="wave"
                    width={`${75 - w}%`}
                    sx={{
                      height: 16,
                      bgcolor: 'rgba(255,255,255,0.06)',
                      borderRadius: 0.5,
                      '&::after': {
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)',
                      },
                    }}
                  />
                </Box>
              ))}
            </>
          ) : (
            <>
              <Link
                className={classes.breakoutTitle}
                to={getMediaPageRoute()}
                id="cyp-media-page-link"
                data-testid="library-card-title"
              >
                <HighlightedText highlightTexts={[searchTerm, hasPhrase, ...hasWords]} text={cardTitle} />
              </Link>
              {visibleFields?.map((field, index) => {
                const fallbackIcon = field.fallbackMessage ? (
                  <Tooltip title={field.fallbackMessage} arrow>
                    <WarningAmberIcon sx={{ fontSize: 14, color: 'text.disabled', ml: 0.5, verticalAlign: 'middle' }} />
                  </Tooltip>
                ) : null;

                return (
                  <Link
                    key={field.slug || index}
                    className={`${classes.cardTitle} ${classes.titleAndValue}`}
                    to={getMediaPageRoute()}
                  >
                    <Typography component="span" className={classes.fieldTitle}>
                      {field.label}
                    </Typography>
                    <span className={classes.fieldSep}>&#8226;</span>
                    <span className={field.show.trim() ? classes.highlightedText : classes.emptyValue}>
                      {field.show.trim() ? (
                        field.slug &&
                        item?.highlights?.[field.slug]?.[0] &&
                        !(DATE_FIELD_TYPES as readonly string[]).includes(field.dataType ?? '') ? (
                          <HighlightedFragment fragment={item.highlights[field.slug][0]} />
                        ) : (
                          <HighlightedText highlightTexts={[searchTerm, hasPhrase, ...hasWords]} text={field.show} />
                        )
                      ) : (
                        '···'
                      )}
                    </span>
                    {fallbackIcon && <span className={classes.fallbackIcon}>{fallbackIcon}</span>}
                  </Link>
                );
              })}
            </>
          )}
        </CardContent>
        {item?.highlights && Object.keys(item.highlights).length > 0 && (
          <MatchContext highlights={item.highlights} slugLabels={item.highlightLabels} />
        )}
        <Collapse className={classes.collapse} in={expanded} timeout="auto" unmountOnExit>
          <ExtendedMetaDataCard itemid={item.id} />
        </Collapse>
        {item?.trExpressions && item.trExpressions.length > 0 && (
          <div className={classes.transcriptResults}>
            <TranscriptResults
              expressions={item.trExpressions as unknown as { startTime: number; linguistics: { text: string }[] }[]}
              item={{ id: item.id }}
              classes={{
                transcriptHeader: classes.transcriptHeader,
                transcriptExpression: classes.transcriptExpression,
                transcriptTimecode: classes.transcriptTimecode,
                transcriptText: classes.transcriptText,
              }}
            />
          </div>
        )}
        {customSearchURL.get(LAYOUT) !== TWOUP && (
          <IconButton
            className={clsx(classes.expand, {
              [classes.expandOpen]: !(
                viewState.showHorizontal && viewState.horizontalMediaCardIndex === props.akomiMediaCardIndex
              ),
            })}
            onClick={handleExpandClick}
            aria-label="show more"
            size="small"
          >
            <ExpandMoreIcon className={classes.expandMore} />
          </IconButton>
        )}
      </Card>
      {showPreview && (
        <MediaCardPreviewModal itemID={item.id} pagination={true} onClose={() => setShowPreview(false)} />
      )}
    </>
  );
};

export default AkomiMediaCard;
